define([
    'uiComponent',
    'ko',
    '../model/slide-activation-tracker'
], (Component, ko, slideActivation) => {
    "use strict";

    /**
     * @typedef {Object} Chapter
     * @property {string} id chapter's identifier
     * @property {SlideIndex[]} slides chapter's slide indexes
     */

    /**
     * @class ProgressBarComponent
     * @extends {Component}
     * @property {Chapter[]} chapters array of chapter objects
     * @property {SlideIndex[]} slides
     * @property {string[])} segments
     */
    return Component.extend({
        defaults: {
            template: 'Lfi_FactoryTour/progress-bar',
            chapters: [],
            slides: [],
            progress: 0,
            remaining: 0
        },

        initialize: function (config) {
            this._super(config);
            this.slides = this.chapters.map(chap => chap.slides).flat();
            this.progress = ko.observable(this.progress);
            this.remaining = ko.observable(this.remaining);
            this.segments = this.chapters.map(chap => `${chap.slides.length}fr`).join(' ');

            /**
             * @typedef {number} ActivationPosition
             * @typedef {1|-1} ActivationDirection
             * @typedef {array} Activation
             * @property {ActivationPosition} 0 position inside progress bar of activated slide
             * @property {ActivationDirection} 1 direction of scroll for activation event
             */

            /** @var {Activation[]} activations */
            let activations = [];
            let batchHandlerTimeout = null;
            function handleBatch () {
                // using the final captured activation (not necessarily the last in the DOM)
                // find the correct position to mark as activated
                let [position, mainDirection] = activations[0];

                /**
                 * @param {number} carry current presumed new position of the progress bar
                 * @param {ActivationPosition} candidatePosition potential new progress bar position
                 * @param {ActivationDirection} candidateDirection direction of candidatePosition
                 * @returns {number} new position of the progress bar
                 */
                function reducer (carry, [candidatePosition, candidateDirection]) {
                    if (candidateDirection !== mainDirection) {
                        return carry;
                    }
                    if ((mainDirection === 1 && candidatePosition > carry) ||
                        (mainDirection === -1 && candidatePosition < carry)) {
                        carry = candidatePosition;
                    }
                    return carry;
                }

                position = activations.reduce(reducer, position);

                // reset the state
                batchHandlerTimeout = null;
                activations = [];

                this.progress(position);
                this.remaining(this.slides.length - position);
            }


            /**
             * @type {ActivationHandler} actvationHandler
             * @inheritDoc
             */
            const activationHandler = (slideIndex, direction) => {
                const position = this.slides.indexOf(slideIndex) + 1;
                if (position === 0) {
                    return;
                }
                // add the captured activation to the list
                activations.unshift([position, direction]);

                // if a batch handler already exists, we're in a "fast scroll" scenario
                // reset the timeout and set a new one to allow others to be added
                if (batchHandlerTimeout) {
                    clearTimeout(batchHandlerTimeout);
                    batchHandlerTimeout = null;
                }
                batchHandlerTimeout = setTimeout(handleBatch.bind(this), 15);
            };

            slideActivation.onActivation(activationHandler);
        },

        /**
         * @param {Chapter} chapter chapter for which to consider active state
         * @returns {boolean} whether chapter is active
         */
        isChapterActive: function (chapter) {
            let accumulatedSeenSlides = this.progress();
            for (let _chapter of this.chapters) {
                if (_chapter.id === chapter.id) {
                    return accumulatedSeenSlides > 0;
                }

                accumulatedSeenSlides -= _chapter.slides.length;
            }

            return false;
        },

        atEnd: function () {
            return this.progress() === this.slides.length;
        }
    });
});
